import React, { useEffect, useState } from 'react';
import { invoiceAPI, razorpayAPI } from '../services/api';
import { FiFileText, FiClock, FiCheckCircle, FiAlertCircle, FiDollarSign, FiCreditCard } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const STATUS_STYLE = {
  draft:     { bg:'#f1f5f9', txt:'#475569', icon:<FiClock size={12}/> },
  sent:      { bg:'#e0f2fe', txt:'#0369a1', icon:<FiFileText size={12}/> },
  viewed:    { bg:'#ede9fe', txt:'#5b21b6', icon:<FiFileText size={12}/> },
  paid:      { bg:'#d1fae5', txt:'#065f46', icon:<FiCheckCircle size={12}/> },
  overdue:   { bg:'#fee2e2', txt:'#991b1b', icon:<FiAlertCircle size={12}/> },
  cancelled: { bg:'#f1f5f9', txt:'#94a3b8', icon:<FiAlertCircle size={12}/> },
};

const FMT = (n) => '₹' + (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Load Razorpay checkout script dynamically
const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.onload  = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

export default function ClientInvoices() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [paying, setPaying]     = useState(false);

  const load = async () => {
    try {
      const r = await invoiceAPI.myInvoices();
      setInvoices(r.data.invoices || []);
    } catch { toast.error('Failed to load invoices'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handlePayNow = async (inv) => {
    if (paying) return;
    setPaying(true);
    try {
      // 1. Load Razorpay script
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error('Could not load payment gateway. Check your connection.'); setPaying(false); return; }

      // 2. Get Razorpay config (public key)
      const configRes = await razorpayAPI.config();
      const keyId = configRes.data.keyId;
      if (!keyId) { toast.error('Payment gateway not configured. Contact admin.'); setPaying(false); return; }

      // 3. Create order on backend
      const orderRes = await razorpayAPI.createOrder(inv._id || inv.id);
      const { order, invoice: invData } = orderRes.data;

      // 4. Open Razorpay checkout
      const options = {
        key:          keyId,
        amount:       order.amount,
        currency:     order.currency,
        name:         'Project Manager',
        description:  `Invoice ${invData.invoiceNumber}`,
        order_id:     order.id,
        prefill: {
          name:  user?.name  || '',
          email: user?.email || '',
        },
        theme: { color: '#6366f1' },
        handler: async (response) => {
          try {
            await razorpayAPI.verify({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              invoiceId:           inv._id || inv.id,
            });
            toast.success('✅ Payment successful! Invoice marked as paid.');
            setSelected(null);
            load();
          } catch { toast.error('Payment verification failed. Contact admin.'); }
        },
        modal: {
          ondismiss: () => { toast('Payment cancelled.', { icon: '⚠️' }); setPaying(false); }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to initiate payment');
    }
    setPaying(false);
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <div style={{ width:40, height:40, border:'3px solid #e2e8f0', borderTop:'3px solid var(--pm-primary)', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const totalDue  = invoices.filter(i => !['paid','cancelled'].includes(i.status)).reduce((s,i) => s + (parseFloat(i.amountInINR||i.total)||0), 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s,i) => s + (parseFloat(i.amountInINR||i.total)||0), 0);
  const overdue   = invoices.filter(i => i.status === 'overdue').length;
  const canPay    = (inv) => !['paid','cancelled'].includes(inv.status);

  const S = {
    card:  { background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px #0000000a', border:'1px solid #f1f5f9' },
    badge: (st) => { const s=STATUS_STYLE[st]||STATUS_STYLE.draft; return { display:'inline-flex', alignItems:'center', gap:4, padding:'3px 10px', borderRadius:8, fontSize:11, fontWeight:700, background:s.bg, color:s.txt }; },
    btn:   (v) => ({ padding:'8px 16px', borderRadius:9, border:'none', fontWeight:600, fontSize:13, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6,
      ...(v==='primary' ? { background:'var(--pm-gradient)', color:'#fff', boxShadow:'0 4px 12px rgba(99,102,241,.25)' }
        : v==='pay'     ? { background:'linear-gradient(135deg,#22c55e,#16a34a)', color:'#fff', boxShadow:'0 4px 12px rgba(34,197,94,.3)' }
        :                 { background:'#f1f5f9', color:'#475569' }) }),
  };

  return (
    <div>
      <div style={{ marginBottom:28 }}>
        <h1 style={{ margin:0, fontSize:28, fontWeight:800, color:'#0f172a' }}>💰 My Invoices</h1>
        <p style={{ margin:'4px 0 0', color:'#94a3b8', fontSize:14 }}>View all invoices and payment status from your projects</p>
      </div>

      {/* KPI Strip */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:16, marginBottom:28 }}>
        {[
          { icon:<FiFileText size={20}/>,    label:'Total Invoices', value:invoices.length,  color:'var(--pm-primary)' },
          { icon:<FiDollarSign size={20}/>,  label:'Amount Due',     value:FMT(totalDue),    color:'#f59e0b' },
          { icon:<FiCheckCircle size={20}/>, label:'Total Paid',     value:FMT(totalPaid),   color:'#22c55e' },
          { icon:<FiAlertCircle size={20}/>, label:'Overdue',        value:overdue,           color:'#ef4444' },
        ].map(s => (
          <div key={s.label} style={{ ...S.card, borderLeft:`4px solid ${s.color}`, display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:42, height:42, borderRadius:11, background:`${s.color}15`, display:'flex', alignItems:'center', justifyContent:'center', color:s.color, flexShrink:0 }}>
              {s.icon}
            </div>
            <div>
              <div style={{ fontSize:20, fontWeight:800, color:'#0f172a' }}>{s.value}</div>
              <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Invoice List */}
      <div style={S.card}>
        <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>All Invoices</h3>

        {invoices.length === 0 ? (
          <div style={{ textAlign:'center', padding:'48px 0', color:'#94a3b8' }}>
            <FiFileText size={48} style={{ marginBottom:12, opacity:0.3 }}/>
            <p style={{ margin:0, fontSize:14 }}>No invoices found yet.</p>
          </div>
        ) : (
          <div>
            {/* Header */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 120px 140px 100px 160px', gap:12, padding:'8px 12px', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', borderBottom:'2px solid #f1f5f9', marginBottom:8 }}>
              <span>Invoice</span><span>Date</span><span>Amount</span><span>Status</span><span>Action</span>
            </div>

            {invoices.map(inv => {
              const st = STATUS_STYLE[inv.status] || STATUS_STYLE.draft;
              return (
                <div key={inv._id} style={{ display:'grid', gridTemplateColumns:'1fr 120px 140px 100px 160px', gap:12, padding:'14px 12px', borderBottom:'1px solid #f8fafc', alignItems:'center', transition:'background .15s' }}
                  onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                  onMouseLeave={e=>e.currentTarget.style.background=''}>
                  <div style={{ cursor:'pointer' }} onClick={() => setSelected(inv)}>
                    <div style={{ fontWeight:700, fontSize:14, color:'#0f172a' }}>{inv.invoiceNumber || `INV-${inv._id}`}</div>
                    <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>{inv.projectName || inv.title || '—'}</div>
                  </div>
                  <div style={{ fontSize:13, color:'#475569' }}>
                    {inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : '—'}
                  </div>
                  <div style={{ fontWeight:700, fontSize:14, color:'#0f172a' }}>{FMT(inv.amountInINR || inv.total)}</div>
                  <div><span style={S.badge(inv.status)}>{st.icon} {inv.status}</span></div>
                  <div style={{ display:'flex', gap:6 }}>
                    <button style={S.btn('ghost')} onClick={() => setSelected(inv)}>View</button>
                    {canPay(inv) && (
                      <button style={{ ...S.btn('pay'), fontSize:12, padding:'7px 12px' }}
                        onClick={() => handlePayNow(inv)} disabled={paying}>
                        <FiCreditCard size={12}/> Pay Now
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Invoice Detail Modal */}
      {selected && (
        <div style={{ position:'fixed', inset:0, background:'#00000060', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
          onClick={e => e.target === e.currentTarget && setSelected(null)}>
          <div style={{ background:'#fff', borderRadius:20, padding:36, maxWidth:560, width:'90%', maxHeight:'85vh', overflowY:'auto', boxShadow:'0 24px 64px #00000022' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
              <div>
                <div style={{ fontSize:20, fontWeight:800, color:'#0f172a' }}>{selected.invoiceNumber || `INV-${selected._id}`}</div>
                <div style={{ fontSize:13, color:'#94a3b8', marginTop:4 }}>{selected.projectName || selected.title || '—'}</div>
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <span style={S.badge(selected.status)}>{STATUS_STYLE[selected.status]?.icon} {selected.status}</span>
                <button style={{ background:'#f1f5f9', border:'none', cursor:'pointer', width:32, height:32, borderRadius:8, fontSize:18, display:'flex', alignItems:'center', justifyContent:'center', color:'#64748b' }} onClick={() => setSelected(null)}>×</button>
              </div>
            </div>

            {/* Info rows */}
            <div style={{ background:'#f8fafc', borderRadius:12, padding:16, marginBottom:20 }}>
              {[
                ['Issue Date', selected.issueDate ? new Date(selected.issueDate).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'}) : '—'],
                ['Due Date',   selected.dueDate   ? new Date(selected.dueDate).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})   : '—'],
                ['Platform',   selected.platform  || '—'],
                ['Currency',   selected.currency  || 'INR'],
              ].map(([k,v]) => (
                <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f1f5f9', fontSize:13 }}>
                  <span style={{ color:'#64748b' }}>{k}</span>
                  <span style={{ fontWeight:600, color:'#0f172a', textTransform:'capitalize' }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Line items */}
            {selected.items?.length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:12, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Items</div>
                {selected.items.map((item, i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f8fafc', fontSize:13 }}>
                    <div>
                      <div style={{ fontWeight:600, color:'#0f172a' }}>{item.description}</div>
                      {item.quantity && <div style={{ fontSize:11, color:'#94a3b8' }}>{item.quantity} × {item.rate}</div>}
                    </div>
                    <div style={{ fontWeight:700, color:'#0f172a' }}>{FMT(item.amount)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Total */}
            <div style={{ background:'var(--pm-primary-lighter,#ede9fe)', borderRadius:12, padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: canPay(selected) ? 16 : 0 }}>
              <span style={{ fontWeight:700, fontSize:15, color:'#0f172a' }}>Total Amount</span>
              <span style={{ fontWeight:800, fontSize:20, color:'var(--pm-primary)' }}>{FMT(selected.amountInINR || selected.total)}</span>
            </div>

            {/* Pay Now button in modal */}
            {canPay(selected) && (
              <button style={{ ...S.btn('pay'), width:'100%', justifyContent:'center', padding:'14px', fontSize:15 }}
                onClick={() => handlePayNow(selected)} disabled={paying}>
                <FiCreditCard size={16}/>
                {paying ? 'Opening payment gateway…' : 'Pay Now via Razorpay'}
              </button>
            )}

            {selected.notes && (
              <div style={{ marginTop:16, padding:14, background:'#fffbeb', borderRadius:10, border:'1px solid #fde68a', fontSize:13, color:'#92400e' }}>
                📝 {selected.notes}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
